import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, Mail, MessageCircle,
  MonitorPlay, Briefcase, FileText, Users,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/sections/Footer";
import DemoModal from "@/components/DemoModal";
import WhatsAppAssistant from "@/components/WhatsAppAssistant";
import Seo from "@/components/Seo";
import { getConfig, postLead } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

const FEATURE_ICONS = [MonitorPlay, Briefcase, FileText, Users];

const AcademyPage = () => {
  const { t, lang } = useLanguage();
  const [whatsapp, setWhatsapp] = useState("");
  const [demoOpen, setDemoOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const openDemo = useCallback(() => setDemoOpen(true), []);

  const a = t.academy;
  const p = t.academyPage;

  const submitEmail = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await postLead({ name: "", email, topic: "academy", source: "academy", lang });
      toast.success(p.success);
      setEmail("");
    } catch {
      toast.error(p.error);
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    getConfig()
      .then((c) => setWhatsapp(c.whatsapp_number || ""))
      .catch(() => {});
  }, []);

  const waLink = useCallback(
    (text) =>
      `https://wa.me/${(whatsapp || "41782042172").replace(/\D/g, "")}?text=${encodeURIComponent(text)}`,
    [whatsapp]
  );

  return (
    <div className="bg-void text-white font-body min-h-screen" data-testid="academy-page">
      <Seo title={t.seo.academy.title} description={t.seo.academy.desc} path="/academy" lang={lang} />
      <div className="grain-overlay" />
      <Navbar onOpenDemo={openDemo} waLink={waLink} />
      <main className="pt-16">
        <section className="pt-12 pb-16 lg:pt-16 lg:pb-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <a
                href="/"
                data-testid="academy-back-link"
                className="inline-flex items-center gap-2 text-sm text-neon-cyan hover:gap-3 transition-all mb-10"
              >
                <ArrowLeft size={16} /> {p.back}
              </a>
              <span className="inline-block text-xs font-bold tracking-widest uppercase border border-neon-cyan/40 text-neon-cyan rounded-full px-3 py-1 mb-6">
                {a.overline}
              </span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight max-w-4xl" data-testid="academy-title">
                {a.titleA} <span className="neon-text">{a.titleNeon}</span>
              </h1>
              <p className="mt-6 text-base lg:text-lg text-white/70 max-w-3xl" data-testid="academy-subtitle">
                {a.subtitle}
              </p>
            </motion.div>

            <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {a.bullets.map((b, i) => {
                const Icon = FEATURE_ICONS[i % FEATURE_ICONS.length];
                return (
                  <motion.div
                    key={b}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 + i * 0.1 }}
                    data-testid={`academy-feature-${i}`}
                    className="glass-panel spotlight rounded-2xl p-7 hover:-translate-y-1.5 hover:shadow-[0_0_40px_rgba(0,240,255,0.12)] transition-all duration-500"
                  >
                    <Icon size={24} className="text-neon-cyan mb-4" />
                    <p className="text-sm text-white/80 leading-relaxed">{b}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-16 lg:py-20" data-testid="academy-program-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mb-12"
            >
              <p className="text-xs font-bold tracking-[0.3em] uppercase text-neon-cyan mb-4">{p.programOverline}</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
                {p.programTitle}
              </h2>
              <p className="mt-5 text-base lg:text-lg text-white/70">{p.programSubtitle}</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {p.lessons.map((lesson, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: (i % 2) * 0.08 }}
                  data-testid={`academy-lesson-${i}`}
                  className="glass-panel spotlight rounded-xl px-6 py-5 flex items-start gap-5 hover:border-neon-cyan/30 transition-colors duration-300"
                >
                  <span className="font-mono text-neon-cyan text-lg font-bold shrink-0 mt-0.5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <p className="text-white font-semibold text-sm sm:text-base">{lesson.title}</p>
                    <p className="text-white/60 text-sm mt-1.5 leading-relaxed">{lesson.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="glass-panel spotlight rounded-3xl p-10 sm:p-14 relative overflow-hidden"
            >
              <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-neon-violet/20 blur-[100px] pointer-events-none" />
              <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-neon-cyan/20 blur-[100px] pointer-events-none" />
              <div className="relative max-w-3xl">
                <p className="text-xs font-bold tracking-[0.3em] uppercase text-neon-cyan mb-4">{p.ctaOverline}</p>
                <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
                  {p.ctaTitle}
                </h2>
                <p className="mt-5 text-base lg:text-lg text-white/70">{p.ctaText}</p>
                <form onSubmit={submitEmail} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-xl" data-testid="academy-email-form">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={p.emailPh}
                    data-testid="academy-email-input"
                    className="flex-1 bg-white/5 border border-white/15 rounded-xl px-5 py-4 text-white placeholder:text-white/40 focus:border-neon-cyan/60 focus:outline-none transition-colors"
                  />
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="academy-email-submit"
                    className="flex items-center justify-center gap-2 btn-cyber bg-gradient-to-r from-neon-cyan to-neon-violet px-8 py-4 font-semibold text-void hover:opacity-90 disabled:opacity-50 transition-all duration-300"
                  >
                    <Mail size={18} /> {sending ? p.sending : p.submit}
                  </button>
                </form>
                <p className="mt-3 text-xs text-white/50">
                  {p.privacyNote} <a href="/privacy" className="underline hover:text-neon-cyan transition-colors">Privacy Policy</a>
                </p>
                <div className="mt-6">
                  <a
                    href={waLink(a.waMessage)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="academy-cta-whatsapp"
                    className="inline-flex items-center gap-2 btn-cyber border border-white/20 px-8 py-4 font-semibold hover:border-neon-cyan/60 hover:text-neon-cyan hover:-translate-y-0.5 transition-all duration-300"
                  >
                    <MessageCircle size={18} /> {a.ctaSecondary}
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer waLink={waLink} />
      <WhatsAppAssistant whatsapp={whatsapp} />
      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
};

export default AcademyPage;
